import React, { useEffect, useState, useContext } from "react";
import Modal from "react-modal";
import { AuthContext } from "../AuthContext/AuthContext";
import ModalPrivileges from "./ModalPrivileges";
import Swal from "sweetalert2";
import "./ModalUser.css";

Modal.setAppElement("#root");

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// ─── Catálogos ──────────────────────────────────────────────────────────────
const TYPE_OPTIONS = [
  { value: "0", label: "Administrador" },
  { value: "1", label: "Cliente" },
  { value: "2", label: "Donador" },
];

const STATUS_OPTIONS = [
  { value: "1", label: "Activo" },
  { value: "0", label: "Inactivo" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const initialFormState = {
  name:     "",
  username: "",
  type:     "1",  // default: Cliente
  status:   "1",  // default: Activo
};

const generarPassword = (name, username) => {
  if (!name || !username) return "";
  const inicial        = name.trim().charAt(0).toUpperCase();
  const usernameLimpio = username.trim().replace(/\s+/g, "");
  return `${inicial}${usernameLimpio}.hm`;
};

// ─── Componente ──────────────────────────────────────────────────────────────
const ModalUser = ({ isOpen, onRequestClose, userData, isEditMode }) => {
  const { user } = useContext(AuthContext);

  const [formData,              setFormData]              = useState(initialFormState);
  const [errorMessage,          setErrorMessage]          = useState("");
  const [processMessage,        setProcessMessage]        = useState("");
  const [processType,           setProcessType]           = useState("");
  const [isPrivilegesModalOpen, setIsPrivilegesModalOpen] = useState(false);

  // ── Rellena / limpia el formulario ─────────────────────────────────────────
  useEffect(() => {
    if (userData) {
      setFormData({
        name:     userData.name     || "",
        username: userData.username || "",
        type:     userData.type     !== undefined ? String(userData.type)   : "1",
        status:   userData.status   !== undefined ? String(userData.status) : "1",
      });
    } else {
      setFormData(initialFormState);
    }
  }, [userData, isOpen]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = (e) => {
    e?.preventDefault();

    // Validación
    if (
      !formData.name.trim()     ||
      !formData.username.trim() ||
      (formData.type   !== "0" && formData.type   !== "1" && formData.type   !== "2") ||
      (formData.status !== "0" && formData.status !== "1")
    ) {
      setErrorMessage("Todos los campos son obligatorios.");
      return;
    }
    setErrorMessage("");

    const url = isEditMode
      ? `${API_BASE}/api/updateUser`
      : `${API_BASE}/api/insertUser`;

    const payload = {
      formData: {
        name:     formData.name.trim(),
        username: formData.username.trim(),
        type:     Number(formData.type),    // ← número al backend
        status:   Number(formData.status),  // ← número al backend
      },
    };

    if (isEditMode && userData?.id_user) {
      payload.id_user = userData.id_user;
    }

    fetch(url, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!isEditMode && data.success) {
          Swal.fire({
            title: "Usuario registrado",
            html: `
              <p>Username: <strong>${data.username}</strong></p>
              <p>Contraseña inicial: <strong>${data.password}</strong></p>
            `,
            icon: "success",
          });
        }

        setProcessType(data.success ? "success" : "error");
        setProcessMessage(data.message);

        setTimeout(() => {
          setFormData(initialFormState);
          setProcessMessage("");
          setProcessType("");
          onRequestClose();
        }, 1200);
      })
      .catch((err) => {
        console.error("Error:", err);
        setProcessType("error");
        setProcessMessage("Error al procesar.");
        setTimeout(() => onRequestClose(), 1200);
      });
  };

  const handleCloseModal = () => {
    setFormData(initialFormState);
    setErrorMessage("");
    setProcessMessage("");
    setProcessType("");
    onRequestClose();
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleCloseModal}
      className="modal-content"
      overlayClassName="modal-overlay"
    >
      {/* Header */}
      <div className="modal-header">
        <h2>{isEditMode ? "Editar Usuario" : "Agregar Usuario"}</h2>
        <hr />
      </div>

      {/* Body */}
      <div className="modal-body">
        {errorMessage && <p className="error-label">{errorMessage}</p>}

        <form onSubmit={handleFormSubmit}>

          {/* Fila 1 — Nombre / Username */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Nombre</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="username">Usuario</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          {/* Fila 2 — Tipo / Estatus */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">Tipo</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Estatus</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                required
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

        </form>
      </div>

      {/* Actions */}
      <div className="modal-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleCloseModal}
        >
          Cancelar
        </button>

        {isEditMode && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setIsPrivilegesModalOpen(true)}
          >
            Privilegios
          </button>
        )}

        <button
          type="button"
          className="btn btn-success"
          onClick={handleFormSubmit}
        >
          {isEditMode ? "Guardar" : "Agregar"}
        </button>
      </div>

      {/* Modal de privilegios (solo edición) */}
      {isEditMode && isPrivilegesModalOpen && (
        <ModalPrivileges
          isOpen={isPrivilegesModalOpen}
          onRequestClose={() => setIsPrivilegesModalOpen(false)}
          userId={userData.id_user}
          privilegesData={userData.privileges}
        />
      )}

      {/* Mensaje de proceso */}
      {processMessage && (
        <p className={`process-message ${processType}`}>{processMessage}</p>
      )}
    </Modal>
  );
};

export default ModalUser;